import * as THREE from 'three';

/**
 * AudioMixer - Unity風の音楽管理システム
 * 複数の音楽グループを管理し、個別に音量調整やエフェクトを適用可能
 */
export default class AudioMixer {
    constructor(audioListener) {
        this.audioListener = audioListener;
        this.audioContext = audioListener.context;
        
        // 音楽グループ（Unity のAudio Mixer Group相当）
        this.groups = new Map();
        this.masterVolume = 1.0;
        
        // マスター出力ノード
        this.masterGain = this.audioContext.createGain();
        this.masterGain.connect(this.audioContext.destination);
        this.masterGain.gain.value = this.masterVolume;
        
        // デフォルトグループを作成
        // this.createGroup('Master');
        // this.createGroup('BGM', 'Master');
        // this.createGroup('SFX', 'Master');
        // this.createGroup('Voice', 'Master');
        // this.createGroup('UI', 'SFX');
        
        // 現在再生中の音楽を管理
        this.playingAudios = new Set();
        
        // ミュート状態
        this.isMuted = false;
        this.previousMasterVolume = 1.0;
    }

    /**
     * 音楽グループを作成
     * @param {string} name - グループ名
     * @param {string} parentName - 親グループ名（省略時はMasterに接続）
     */
    createGroup(name, parentName = null) {
        if (this.groups.has(name)) {
            console.warn(`Audio group "${name}" already exists`);
            return;
        }

        const gainNode = this.audioContext.createGain();
        gainNode.gain.value = 1.0;

        const group = {
            name,
            gainNode,
            volume: 1.0,
            isMuted: false,
            previousVolume: 1.0,
            parent: parentName,
            children: new Set(),
            effects: []
        };

        // 親グループに接続
        if (parentName && this.groups.has(parentName)) {
            const parentGroup = this.groups.get(parentName);
            gainNode.connect(parentGroup.gainNode);
            parentGroup.children.add(name);
        } else if (name !== 'Master') {
            // Masterグループ以外は自動的にMasterに接続
            gainNode.connect(this.masterGain);
            if (this.groups.has('Master')) {
                this.groups.get('Master').children.add(name);
            }
        } else {
            // Masterグループの場合
            gainNode.connect(this.masterGain);
        }

        this.groups.set(name, group);

        console.log(`Audio group "${name}" created`);
    }

    /**
     * 音楽を再生
     * @param {AudioBuffer} audioBuffer - 音楽データ
     * @param {Object} options - 再生オプション
     */
    playAudio(audioBuffer, options = {}) {
        const {
            group = 'SFX',
            volume = 1.0,
            loop = false,
            pitch = 1.0,
            delay = 0,
            position = null, // {x, y, z} for 3D audio
            fadeInDuration = 0,
            onComplete = null
        } = options;

        if (!this.groups.has(group)) {
            console.error(`Audio group "${group}" not found`);
            return null;
        }

        const audioGroup = this.groups.get(group);
        let audio;

        // 3D音楽か通常音楽かを判定
        if (position) {
            audio = new THREE.PositionalAudio(this.audioListener);
            audio.position.set(position.x, position.y, position.z);
        } else {
            audio = new THREE.Audio(this.audioListener);
        }

        audio.setBuffer(audioBuffer);
        audio.setLoop(loop);
        audio.setVolume(volume);
        audio.setPlaybackRate(pitch);

        // Web Audio APIのソースノードを取得してグループに接続
        const source = audio.source;
        if (source) {
            // Three.jsのAudioからWeb Audio APIのノードを切断
            source.disconnect();
            
            // 音量調整用のゲインノードを作成
            const audioGain = this.audioContext.createGain();
            audioGain.gain.value = volume;
            
            // 接続: source -> audioGain -> group -> master
            source.connect(audioGain);
            audioGain.connect(audioGroup.gainNode);
            
            // フェードイン
            if (fadeInDuration > 0) {
                audioGain.gain.setValueAtTime(0, this.audioContext.currentTime);
                audioGain.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + fadeInDuration);
            }
        }

        // 遅延再生
        if (delay > 0) {
            setTimeout(() => {
                audio.play();
            }, delay * 1000);
        } else {
            audio.play();
        }

        // 再生管理
        const audioInfo = {
            audio,
            group,
            gainNode: source ? audio.source : null,
            volumeNode: source ? audioGain : null,
            startTime: this.audioContext.currentTime + delay,
            options
        };

        this.playingAudios.add(audioInfo);

        // 再生終了時の処理
        audio.onEnded = () => {
            this.playingAudios.delete(audioInfo);
            if (onComplete) onComplete();
        };

        return audioInfo;
    }

    /**
     * BGMを再生（簡単なヘルパー）
     */
    playBGM(audioBuffer, volume = 0.5, fadeInDuration = 2.0) {
        // 既存のBGMを停止
        this.stopGroup('BGM', fadeInDuration);
        
        return this.playAudio(audioBuffer, {
            group: 'BGM',
            volume,
            loop: true,
            fadeInDuration
        });
    }

    /**
     * BGMを再生（簡単なヘルパー）
     */
    playBonfireSound(audioBuffer, volume = 0.5, fadeInDuration = 2.0) {
      // 既存のBGMを停止
      this.stopGroup('Bonfire', fadeInDuration);
      
      return this.playAudio(audioBuffer, {
          group: 'Bonfire',
          volume,
          loop: true,
          fadeInDuration
      });
  }

    /**
     * 効果音を再生（簡単なヘルパー）
     */
    playSFX(audioBuffer, volume = 1.0, pitch = 1.0) {
        return this.playAudio(audioBuffer, {
            group: 'SFX',
            volume,
            pitch,
            loop: false
        });
    }

    /**
     * 3D効果音を再生
     */
    play3DSFX(audioBuffer, position, volume = 1.0, refDistance = 1) {
        const audioInfo = this.playAudio(audioBuffer, {
            group: 'SFX',
            volume,
            position,
            loop: false
        });
        
        if (audioInfo && audioInfo.audio.setRefDistance) {
            audioInfo.audio.setRefDistance(refDistance);
        }
        
        return audioInfo;
    }

    /**
     * グループの音量を設定
     */
    setGroupVolume(groupName, volume, fadeTime = 0) {
        if (!this.groups.has(groupName)) {
            console.error(`Audio group "${groupName}" not found`);
            return;
        }

        const group = this.groups.get(groupName);
        group.volume = Math.max(0, Math.min(1, volume));

        if (fadeTime > 0) {
            group.gainNode.gain.linearRampToValueAtTime(
                group.volume, 
                this.audioContext.currentTime + fadeTime
            );
        } else {
            group.gainNode.gain.value = group.volume;
        }
    }

    /**
     * グループをミュート/ミュート解除
     */
    muteGroup(groupName, mute = true) {
        if (!this.groups.has(groupName)) {
            console.error(`Audio group "${groupName}" not found`);
            return;
        }

        const group = this.groups.get(groupName);
        
        if (mute && !group.isMuted) {
            group.previousVolume = group.volume;
            group.isMuted = true;
            group.gainNode.gain.value = 0;
        } else if (!mute && group.isMuted) {
            group.isMuted = false;
            group.gainNode.gain.value = group.previousVolume;
        }
    }

    /**
     * グループの音楽を停止
     */
    stopGroup(groupName, fadeOutDuration = 0) {
        this.playingAudios.forEach(audioInfo => {
            if (audioInfo.group === groupName) {
                if (fadeOutDuration > 0 && audioInfo.volumeNode) {
                    audioInfo.volumeNode.gain.linearRampToValueAtTime(
                        0, 
                        this.audioContext.currentTime + fadeOutDuration
                    );
                    setTimeout(() => {
                        audioInfo.audio.stop();
                    }, fadeOutDuration * 1000);
                } else {
                    audioInfo.audio.stop();
                }
            }
        });
    }

    /**
     * マスター音量を設定
     */
    setMasterVolume(volume, fadeTime = 0) {
        this.masterVolume = Math.max(0, Math.min(1, volume));

        if (fadeTime > 0) {
            this.masterGain.gain.linearRampToValueAtTime(
                this.masterVolume,
                this.audioContext.currentTime + fadeTime
            );
        } else {
            this.masterGain.gain.value = this.masterVolume;
        }
    }

    /**
     * 全体をミュート/ミュート解除
     */
    setMasterMute(mute = true) {
        if (mute && !this.isMuted) {
            this.previousMasterVolume = this.masterVolume;
            this.isMuted = true;
            this.masterGain.gain.value = 0;
        } else if (!mute && this.isMuted) {
            this.isMuted = false;
            this.masterGain.gain.value = this.previousMasterVolume;
        }
    }

    /**
     * 全ての音楽を停止
     */
    stopAll(fadeOutDuration = 0) {
        this.playingAudios.forEach(audioInfo => {
            if (fadeOutDuration > 0 && audioInfo.volumeNode) {
                audioInfo.volumeNode.gain.linearRampToValueAtTime(
                    0,
                    this.audioContext.currentTime + fadeOutDuration
                );
                setTimeout(() => {
                    audioInfo.audio.stop();
                }, fadeOutDuration * 1000);
            } else {
                audioInfo.audio.stop();
            }
        });
    }

    /**
     * 現在の状態を取得（デバッグ用）
     */
    getStatus() {
        const status = {
            masterVolume: this.masterVolume,
            isMuted: this.isMuted,
            playingCount: this.playingAudios.size,
            groups: {}
        };

        this.groups.forEach((group, name) => {
            status.groups[name] = {
                volume: group.volume,
                isMuted: group.isMuted,
                parent: group.parent,
                children: Array.from(group.children)
            };
        });

        return status;
    }

    /**
     * リソースを解放
     */
    dispose() {
        this.stopAll();
        this.groups.clear();
        this.playingAudios.clear();
        this.masterGain.disconnect();
    }
}